import React from 'react';
import { Alert, Button, Icon, IconButton } from '@mui/material';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON_SMALL } from '../../../../theme/v4Compat';
import {
  startOfWeek,
  endOfWeek,
  addDays,
  parseISO,
  startOfDay,
  isWithinInterval,
  differenceInCalendarDays,
  format,
  getISOWeek
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useFarmDateTime } from '../../../../hooks/useFarmDateTime';
import { ApiService } from '../../../../hooks';
import { HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse } from '../../../../api/HealthSystemApi';

type Schedule = HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse;

const WEEK_STARTS_ON = 1 as const; // Monday

const getWeekAnchor = (schedule: Schedule): Date =>
  startOfWeek(new Date(schedule.startDate), { weekStartsOn: WEEK_STARTS_ON });

const getTotalWeekIndex = (schedule: Schedule, target: Date): number => {
  // Calendar days, not elapsed ms / 24h: across a DST change a Monday midnight is 23h short of a whole
  // number of days after the anchor, which floors to the previous week.
  const daysDiff = differenceInCalendarDays(target, getWeekAnchor(schedule));
  return Math.floor(Math.max(0, daysDiff) / 7);
};

const computeCycleIteration = (schedule: Schedule, target: Date): number =>
  Math.floor(getTotalWeekIndex(schedule, target) / schedule.totalWeeks) + 1;

const computeWeekInCycle = (schedule: Schedule, target: Date): number =>
  (getTotalWeekIndex(schedule, target) % schedule.totalWeeks) + 1;

const getWeekDateRange = (schedule: Schedule, cycleIteration: number, weekNum: number): { start: Date; end: Date } => {
  const anchor = getWeekAnchor(schedule);
  const start = addDays(anchor, ((cycleIteration - 1) * schedule.totalWeeks + (weekNum - 1)) * 7);
  return { start, end: addDays(start, 6) };
};

const getScheduleEndDate = (schedule: Schedule): Date | null => {
  return schedule.endDate ? parseISO(schedule.endDate) : null;
};

const findScheduleForDate = (schedules: Schedule[], date: Date): Schedule | null => {
  const target = startOfDay(date);
  return (
    schedules.find((s) => {
      const start = startOfDay(parseISO(s.startDate));
      const end = getScheduleEndDate(s);
      if (!end) return target >= start;
      return isWithinInterval(target, { start, end });
    }) ?? null
  );
};

interface ScheduleWeekSelectorProps {
  onWeekSelect: (dateFrom: Date, dateTo: Date) => void;
  initialDate?: Date | null;
}

const ScheduleWeekSelector: React.FC<ScheduleWeekSelectorProps> = ({ onWeekSelect, initialDate }) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();
  const apiService = new ApiService();

  const today = React.useMemo(() => startOfDay(new Date()), []);
  const currentWeekStart = React.useMemo(() => startOfWeek(today, { weekStartsOn: WEEK_STARTS_ON }), [today]);
  const currentWeekEnd = React.useMemo(() => endOfWeek(today, { weekStartsOn: WEEK_STARTS_ON }), [today]);

  const [allSchedules, setAllSchedules] = React.useState<Schedule[]>([]);
  const [activeSchedule, setActiveSchedule] = React.useState<Schedule | null>(null);
  const [selectedWeek, setSelectedWeek] = React.useState<number | null>(null);
  const [selectedCycle, setSelectedCycle] = React.useState<number>(1);
  const [loaded, setLoaded] = React.useState(false);

  // The schedule that covers today (used for "current week" highlighting and transition detection)
  const todaySchedule = React.useMemo(() => findScheduleForDate(allSchedules, today), [allSchedules, today]);

  // Detect if a schedule ends within the current calendar week
  const endingSchedule = React.useMemo(() => {
    if (allSchedules.length === 0) return null;
    return (
      allSchedules.find((s) => {
        const end = getScheduleEndDate(s);
        if (!end) return false;
        const endDay = startOfDay(end);
        return endDay >= currentWeekStart && endDay <= currentWeekEnd;
      }) ?? null
    );
  }, [allSchedules, currentWeekStart, currentWeekEnd]);

  // Detect if a new schedule starts within the current calendar week (but not on Monday = week start)
  const startingSchedule = React.useMemo(() => {
    if (allSchedules.length === 0) return null;
    return (
      allSchedules.find((s) => {
        const start = startOfDay(parseISO(s.startDate));
        return start > currentWeekStart && start <= currentWeekEnd;
      }) ?? null
    );
  }, [allSchedules, currentWeekStart, currentWeekEnd]);

  const hasTransitionThisWeek = endingSchedule !== null && startingSchedule !== null;

  React.useEffect(() => {
    apiService.api.taskList
      .taskListGetAllProductionSchedules()
      .then((result) => {
        setAllSchedules(result.data);
      })
      .catch(() => {
        setAllSchedules([]);
      })
      .finally(() => setLoaded(true));
  }, []);

  // Initialize activeSchedule and cycle/week from initialDate (or today) when schedules are loaded
  React.useEffect(() => {
    if (allSchedules.length === 0) return;
    const targetDate = initialDate ? startOfDay(initialDate) : today;
    const targetSchedule = findScheduleForDate(allSchedules, targetDate) ?? todaySchedule;
    if (targetSchedule) {
      setActiveSchedule(targetSchedule);
      setSelectedCycle(computeCycleIteration(targetSchedule, targetDate));
      setSelectedWeek(computeWeekInCycle(targetSchedule, targetDate));
    }
  }, [allSchedules, initialDate, today]);

  // Navigate to a week, resolving which schedule it belongs to
  const navigateToWeek = (schedule: Schedule, cycle: number, weekNum: number) => {
    const { start, end } = getWeekDateRange(schedule, cycle, weekNum);

    // Check if this date range falls under a different schedule
    const targetSchedule = findScheduleForDate(allSchedules, start);

    if (targetSchedule && targetSchedule.id !== schedule.id) {
      // Switched to a different schedule — recalculate cycle/week from that schedule's perspective
      const newCycle = computeCycleIteration(targetSchedule, start);
      const newWeek = computeWeekInCycle(targetSchedule, start);
      setActiveSchedule(targetSchedule);
      setSelectedCycle(newCycle);
      setSelectedWeek(newWeek);
      const adjusted = getWeekDateRange(targetSchedule, newCycle, newWeek);
      onWeekSelect(adjusted.start, adjusted.end);
    } else {
      // Same schedule or no schedule found — keep current
      setSelectedWeek(weekNum);
      onWeekSelect(start, end);
    }
  };

  const handleWeekSelect = (weekNum: number) => {
    if (!activeSchedule) return;
    navigateToWeek(activeSchedule, selectedCycle, weekNum);
  };

  const handleCycleChange = (delta: number) => {
    if (!activeSchedule) return;
    const newCycle = selectedCycle + delta;
    if (newCycle < 1) return;
    setSelectedCycle(newCycle);

    if (selectedWeek !== null) {
      navigateToWeek(activeSchedule, newCycle, selectedWeek);
    }
  };

  const getCycleLabel = (): string => {
    if (!activeSchedule) return '';
    const anchor = getWeekAnchor(activeSchedule);
    const cycleStart = addDays(anchor, (selectedCycle - 1) * activeSchedule.totalWeeks * 7);
    const cycleEnd = addDays(cycleStart, activeSchedule.totalWeeks * 7 - 1);
    // cycleStart/cycleEnd are local midnights; formatted in the farm timezone they can land on the previous
    // day, so format the calendar day at noon UTC instead.
    const formatDay = (d: Date) => formatDate(`${format(d, 'yyyy-MM-dd')}T12:00:00Z`);
    return `${formatDay(cycleStart)} – ${formatDay(cycleEnd)}`;
  };

  if (!loaded || !activeSchedule) return null;

  // "Current week" highlighting uses today's schedule
  const todayWeekInCycle = todaySchedule ? computeWeekInCycle(todaySchedule, today) : null;
  const todayCycleIteration = todaySchedule ? computeCycleIteration(todaySchedule, today) : null;
  const isViewingTodaySchedule = todaySchedule && activeSchedule.id === todaySchedule.id;

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      {/* Schedule info header */}
      <div className="w-full text-xs text-slate-500">
        <span>
          {t('f-tasks-production-schedule')}: {formatDate(activeSchedule.startDate)}
          {getScheduleEndDate(activeSchedule) && ` – ${formatDate(activeSchedule.endDate!)}`}
        </span>
      </div>

      {/* Transition banner — only shown when viewing today's schedule */}
      {isViewingTodaySchedule && hasTransitionThisWeek && (
        <div className="mb-2">
          <Alert severity="warning" variant="outlined" className="!py-0">
            {t('f-tasks-schedule-transition-banner', {
              endDate: formatDate(endingSchedule!.endDate!),
              startDate: formatDate(startingSchedule!.startDate),
              interpolation: { escapeValue: false }
            })}
          </Alert>
        </div>
      )}

      {/* Ending schedule banner (without a new one starting) */}
      {isViewingTodaySchedule && endingSchedule && !startingSchedule && (
        <div className="mb-2">
          <Alert severity="info" variant="outlined" className="!py-0">
            {t('f-tasks-schedule-ending-banner', {
              endDate: formatDate(endingSchedule!.endDate!),
              interpolation: { escapeValue: false }
            })}
          </Alert>
        </div>
      )}

      {/* Cycle navigation */}
      <div className="flex flex-wrap items-center gap-2">
        <IconButton
          aria-label={t('f-task-workspace-previous-cycle', 'Previous cycle')}
          size="small"
          sx={V4_ICON_BUTTON_SMALL}
          onClick={() => handleCycleChange(-1)}
          disabled={selectedCycle <= 1}
        >
          <Icon fontSize="small">chevron_left</Icon>
        </IconButton>
        <span className="text-sm font-semibold text-gray-800">
          {t('f-com-cycle')} {selectedCycle}
        </span>
        <IconButton
          aria-label={t('f-task-workspace-next-cycle', 'Next cycle')}
          size="small"
          sx={V4_ICON_BUTTON_SMALL}
          onClick={() => handleCycleChange(1)}
        >
          <Icon fontSize="small">chevron_right</Icon>
        </IconButton>
        <span className="text-xs text-gray-500">{getCycleLabel()}</span>
      </div>

      {/* Week buttons */}
      <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {Array.from({ length: activeSchedule.totalWeeks }, (_, i) => i + 1).map((week) => {
          const isCurrentWeek =
            isViewingTodaySchedule && week === todayWeekInCycle && selectedCycle === todayCycleIteration;
          const isoWeek = getISOWeek(getWeekDateRange(activeSchedule, selectedCycle, week).start);
          return (
            <Button
              key={week}
              variant={selectedWeek === week ? 'contained' : 'text'}
              size="small"
              aria-pressed={selectedWeek === week}
              color={selectedWeek === week ? 'primary' : 'inherit'}
              sx={selectedWeek === week ? undefined : V4_BUTTON_DEFAULT}
              onClick={() => handleWeekSelect(week)}
            >
              {week} {t('f-com-week')}
              <span className="ml-1 text-xs opacity-70">
                ({t('f-task-workspace-iso-week', { week: isoWeek, defaultValue: 'wk {{week}}' })})
              </span>
              {isCurrentWeek && <span className="ml-1 text-xs font-medium">{t('f-com-week-current')}</span>}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduleWeekSelector;
